import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useT, LanguageSwitcher } from "@/lib/i18n";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, ChevronRight, Globe2, Languages, Gamepad2, AtSign, X } from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/Footer";

export default function OnboardingPage() {
  const { t } = useT();
  const STEPS = [
    t("onb.nickname_step"),
    t("page.profile.country"),
    t("page.profile.language"),
    t("page.profile.games"),
    t("onb.avatar_step") || "Avatar",
  ];

  const AVATAR_PRESETS = [
    { id: "neon-orbit",      emoji: "🛰️", color: "from-cyan-400 to-blue-600" },
    { id: "cyber-samurai",   emoji: "🗡️", color: "from-rose-500 to-red-700" },
    { id: "pixel-ghost",     emoji: "👾", color: "from-violet-500 to-purple-700" },
    { id: "mecha-pilot",     emoji: "🤖", color: "from-zinc-300 to-zinc-600" },
    { id: "zombie-hunter",   emoji: "🧟", color: "from-emerald-500 to-emerald-800" },
    { id: "arctic-wolf",     emoji: "🐺", color: "from-sky-300 to-blue-700" },
    { id: "phoenix-agent",   emoji: "🔥", color: "from-orange-400 to-red-600" },
    { id: "void-walker",     emoji: "🌌", color: "from-indigo-500 to-violet-900" },
    { id: "retro-hero",      emoji: "🎮", color: "from-yellow-400 to-orange-600" },
    { id: "obsidian-sniper", emoji: "🎯", color: "from-zinc-700 to-black" },
    { id: "valkyrie",        emoji: "⚔️", color: "from-amber-400 to-yellow-700" },
    { id: "glitch-wizard",   emoji: "✨", color: "from-pink-400 to-fuchsia-700" },
  ];

  const { user, saveProfile, setNickname } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [games, setGames] = useState([]);
  const [query, setQuery] = useState("");

  const [nickname, setNicknameLocal] = useState(user?.nickname || "");
  const [nickCheck, setNickCheck] = useState({ status: "idle", reason: null });
  const [suggestions, setSuggestions] = useState([]);
  const [country, setCountry] = useState(null);
  const [language, setLanguage] = useState(null);
  const [selectedGames, setSelectedGames] = useState([]);
  const [avatar, setAvatar] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, l, g] = await Promise.all([
          api.get("/meta/countries"),
          api.get("/meta/languages"),
          api.get("/games"),
        ]);
        setCountries(c.data.items);
        setLanguages(l.data.items);
        setGames(g.data.items);
      } catch (e) {
        toast.error("Failed to load options");
      }
    })();
  }, []);

  // Debounced nickname uniqueness check
  useEffect(() => {
    if (step !== 0) return;
    const nick = nickname.trim();
    setSuggestions([]); // clear stale suggestions whenever the input changes
    if (nick.length === 0) {
      setNickCheck({ status: "idle", reason: null });
      return;
    }
    if (nick.length < 3) {
      setNickCheck({ status: "invalid", reason: "too_short" });
      return;
    }
    if (nick.length > 20) {
      setNickCheck({ status: "invalid", reason: "too_long" });
      return;
    }
    if (!/^[A-Za-z0-9_.\-]+$/.test(nick)) {
      setNickCheck({ status: "invalid", reason: "invalid_chars" });
      return;
    }
    setNickCheck({ status: "checking", reason: null });
    const handle = setTimeout(async () => {
      try {
        const res = await api.post("/profile/nickname/check", {
          nickname: nick,
          user_id: user?.id,
        });
        if (res.data.available) {
          setNickCheck({ status: "available", reason: null });
        } else {
          const reason = res.data.reason || "taken";
          setNickCheck({ status: "taken", reason });
          // Fetch smart variants for conflicts — not for format errors.
          if (["taken", "reserved", "blocked"].includes(reason)) {
            try {
              const sug = await api.get(
                `/profile/nickname/suggest?base=${encodeURIComponent(nick)}`
              );
              setSuggestions(sug.data?.suggestions || []);
            } catch {
              setSuggestions([]);
            }
          }
        }
      } catch {
        setNickCheck({ status: "idle", reason: null });
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [nickname, step, user?.id]);

  const filteredCountries = useMemo(() => {
    if (!query) return countries;
    return countries.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));
  }, [countries, query]);

  const canNext =
    (step === 0 && nickCheck.status === "available") ||
    (step === 1 && country) ||
    (step === 2 && language) ||
    (step === 3 && selectedGames.length > 0) ||
    step === 4; // avatar is optional

  const handleNext = async () => {
    if (step < 4) {
      setStep(step + 1);
      setQuery("");
      return;
    }
    try {
      const payload = {
        user_id: user.id,
        country: country.code,
        language: language.code,
        languages: [language.code],
        games: selectedGames,
        nickname: nickname.trim(),
        avatar: avatar || "",
      };
      await api.post("/onboarding", payload);
      setNickname(nickname.trim());
      saveProfile({
        country: country.code,
        countryName: country.name,
        flag: country.flag,
        language: language.code,
        languageName: language.name,
        languages: [language.code],
        games: selectedGames,
        nickname: nickname.trim(),
        avatar: avatar || "",
        completed_onboarding: true,
      });
      toast.success(t("onb.all_set"));
      navigate("/app");
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error(t("onb.nick_taken"));
        setStep(0);
      } else {
        toast.error(t("onb.save_failed"));
      }
    }
  };

  const toggleGame = (g) => {
    setSelectedGames((prev) =>
      prev.includes(g.name) ? prev.filter((x) => x !== g.name) : [...prev, g.name]
    );
  };

  const nickStatusText = (() => {
    if (nickCheck.status === "idle") return "";
    if (nickCheck.status === "checking") return t("onb.nick_checking");
    if (nickCheck.status === "available") return t("onb.nick_available");
    if (nickCheck.reason === "too_short") return t("onb.nick_too_short");
    if (nickCheck.reason === "too_long") return t("onb.nick_too_long");
    if (nickCheck.reason === "invalid_chars") return t("onb.nick_invalid");
    if (nickCheck.reason === "reserved")
      return t("onb.nick_reserved") || "Dieser Name ist reserviert.";
    if (nickCheck.reason === "blocked")
      return t("onb.nick_blocked") || "Dieser Name ist nicht erlaubt.";
    return t("onb.nick_taken");
  })();

  const nickStatusColor = (() => {
    if (nickCheck.status === "available") return "text-emerald-400";
    if (nickCheck.status === "checking") return "text-zinc-400";
    if (nickCheck.status === "idle") return "text-zinc-500";
    return "text-rose-400";
  })();

  return (
    <div className="min-h-screen bg-radial-neon text-white" data-testid="onboarding-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-28 sm:pb-10">
        <div className="flex items-center justify-between mb-10">
          <Logo to="/" />
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <span className="hidden sm:inline text-xs text-zinc-500 font-mono">
              {t("onb.step_of", { current: step + 1, total: STEPS.length })}
            </span>
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 sm:gap-3 mb-8">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2 sm:gap-3 flex-1">
              <div
                className={`h-9 w-9 rounded-full grid place-items-center text-xs font-bold border ${
                  i <= step
                    ? "bg-neon text-black border-neon"
                    : "bg-zinc-900 text-zinc-500 border-white/10"
                }`}
              >
                {i < step ? <Check size={16} /> : i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-zinc-400 truncate">{label}</div>
                <div className={`h-1 mt-1 rounded-full ${i <= step ? "bg-neon" : "bg-white/5"}`} />
              </div>
            </div>
          ))}
        </div>

        {/* STEP 0: Nickname */}
        {step === 0 && (
          <section data-testid="onboarding-nickname-step">
            <div className="flex items-center gap-3 mb-4">
              <AtSign className="text-neon" />
              <h1 className="font-display text-3xl font-black">{t("onb.nickname_h")}</h1>
            </div>
            <p className="text-zinc-400 mb-5">{t("onb.nickname_sub")}</p>
            <div className="relative max-w-md">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">@</span>
              <Input
                autoFocus
                placeholder="NightHawk_42"
                value={nickname}
                onChange={(e) => setNicknameLocal(e.target.value)}
                maxLength={20}
                className="h-14 pl-9 pr-10 rounded-xl bg-zinc-900 border-white/10 focus-visible:border-neon focus-visible:ring-neon text-lg font-display font-bold"
                data-testid="onboarding-nickname-input"
              />
              {nickname && (
                <button
                  type="button"
                  onClick={() => setNicknameLocal("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  aria-label="clear"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <p className={`mt-2 text-xs font-medium ${nickStatusColor}`} data-testid="nickname-status">
              {nickStatusText || t("onb.nick_hint")}
            </p>
            {suggestions.length > 0 && nickCheck.status === "taken" && (
              <div className="mt-3" data-testid="nickname-suggestions">
                <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">
                  {t("onb.nick_try") || "Wie wär's mit"}
                </p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setNicknameLocal(s)}
                      data-testid={`nickname-suggestion-${s}`}
                      className="px-3 py-1.5 rounded-full text-xs font-semibold bg-neon/10 text-neon border border-neon/30 hover:bg-neon/20 transition"
                    >
                      @{s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* STEP 1: Country */}
        {step === 1 && (
          <section data-testid="onboarding-country-step">
            <div className="flex items-center gap-3 mb-4">
              <Globe2 className="text-neon" />
              <h1 className="font-display text-3xl font-black">{t("onb.country_h")}</h1>
            </div>
            <p className="text-zinc-400 mb-5">{t("onb.country_sub")}</p>
            <Input
              placeholder={t("onb.country_search")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-12 rounded-xl bg-zinc-900 border-white/10 mb-4 focus-visible:border-neon focus-visible:ring-neon"
              data-testid="onboarding-country-search"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[420px] overflow-y-auto pr-1">
              {filteredCountries.map((c) => (
                <button
                  key={c.code}
                  onClick={() => setCountry(c)}
                  data-testid={`country-${c.code}`}
                  className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-all ${
                    country?.code === c.code
                      ? "border-neon bg-neon/10 shadow-neon"
                      : "border-white/5 bg-zinc-900/60 hover:border-white/15"
                  }`}
                >
                  <span className="text-2xl">{c.flag}</span>
                  <span className="font-medium">{c.name}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* STEP 2: Language */}
        {step === 2 && (
          <section data-testid="onboarding-language-step">
            <div className="flex items-center gap-3 mb-4">
              <Languages className="text-neon" />
              <h1 className="font-display text-3xl font-black">{t("onb.lang_h")}</h1>
            </div>
            <p className="text-zinc-400 mb-5">{t("onb.lang_sub")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {languages.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLanguage(l)}
                  data-testid={`language-${l.code}`}
                  className={`rounded-2xl border px-4 py-4 text-left transition-all ${
                    language?.code === l.code
                      ? "border-neon bg-neon/10 shadow-neon"
                      : "border-white/5 bg-zinc-900/60 hover:border-white/15"
                  }`}
                >
                  <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">
                    {l.code}
                  </div>
                  <div className="font-display font-bold text-lg mt-1">{l.name}</div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* STEP 3: Games */}
        {step === 3 && (
          <section data-testid="onboarding-games-step">
            <div className="flex items-center gap-3 mb-4">
              <Gamepad2 className="text-neon" />
              <h1 className="font-display text-3xl font-black">{t("onb.games_h")}</h1>
            </div>
            <p className="text-zinc-400 mb-5">{t("onb.games_sub")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {games.map((g) => {
                const active = selectedGames.includes(g.name);
                return (
                  <button
                    key={g.id}
                    onClick={() => toggleGame(g)}
                    data-testid={`game-${g.id}`}
                    className={`relative overflow-hidden rounded-2xl border text-left transition-all ${
                      active
                        ? "border-neon shadow-neon"
                        : "border-white/5 hover:border-white/15"
                    }`}
                  >
                    <img
                      src={g.cover}
                      alt={g.name}
                      className="w-full h-24 object-cover opacity-80"
                      loading="lazy"
                    />
                    <div className="p-3 bg-zinc-900/80">
                      <div className="font-bold text-sm">{g.name}</div>
                      <div className="text-[11px] text-zinc-400">{g.genre}</div>
                    </div>
                    {active && (
                      <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-neon text-black grid place-items-center">
                        <Check size={16} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* STEP 4: Avatar (optional) */}
        {step === 4 && (
          <section data-testid="onboarding-avatar-step">
            <div className="flex items-center gap-3 mb-4">
              <AtSign className="text-neon" />
              <h1 className="font-display text-3xl font-black">
                {t("onb.avatar_h") || "Wähle deinen Avatar"}
              </h1>
            </div>
            <p className="text-zinc-400 mb-2">
              {t("onb.avatar_sub") || "Optional. Wählt eines der 12 Gaming-Presets — ändern kannst du es später jederzeit."}
            </p>
            <div className="flex items-center justify-between gap-2 mb-5">
              <p className="text-[11px] text-zinc-500 flex-1">
                {t("onb.avatar_private") || "Dein echter Name / Google-Profilbild wird nie öffentlich gezeigt."}
              </p>
              <button
                type="button"
                onClick={() => {
                  const pick = AVATAR_PRESETS[Math.floor(Math.random() * AVATAR_PRESETS.length)];
                  setAvatar(pick.id);
                }}
                className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-neon/10 text-neon border border-neon/30 hover:bg-neon/20 transition whitespace-nowrap"
                data-testid="avatar-random-btn"
              >
                {t("onb.avatar_random") || "Zufälliger Pilot"}
              </button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="avatar-picker">
              {AVATAR_PRESETS.map((a) => {
                const active = avatar === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setAvatar(active ? null : a.id)}
                    data-testid={`avatar-${a.id}`}
                    className={`relative aspect-square rounded-2xl border-2 grid place-items-center transition-all duration-200 ${
                      active
                        ? "border-neon shadow-neon scale-[1.05] ring-2 ring-neon/40"
                        : "border-white/10 hover:border-neon/40 hover:scale-[1.03] hover:shadow-[0_0_24px_-4px_rgba(0,229,255,0.35)]"
                    } bg-gradient-to-br ${a.color}`}
                  >
                    <span className="text-4xl drop-shadow-lg">{a.emoji}</span>
                    {active && (
                      <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-neon text-black grid place-items-center">
                        <Check size={14} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setAvatar(null)}
              className="mt-4 text-xs text-zinc-400 underline-offset-4 hover:text-zinc-200"
              data-testid="avatar-skip"
            >
              {t("onb.avatar_skip") || "Später aussuchen, bitte weiter"}
            </button>
          </section>
        )}

        {/* Actions */}
        <div className="mt-10 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            className="text-zinc-400 hover:text-white hover:bg-white/5"
            onClick={() => (step > 0 ? setStep(step - 1) : navigate("/login"))}
            data-testid="onboarding-back-btn"
          >
            {t("onb.back")}
          </Button>
          <Button
            disabled={!canNext}
            onClick={handleNext}
            className="h-12 px-7 rounded-full bg-neon text-black hover:bg-neon-hover font-bold shadow-neon disabled:opacity-50 disabled:shadow-none"
            data-testid="onboarding-next-btn"
          >
            {step === 4 ? t("onb.enter") : t("onb.continue")}
            <ChevronRight size={18} className="ml-1" />
          </Button>
        </div>
        <Footer variant="compact" />
      </div>
    </div>
  );
}
